import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, X } from "@/lib/icons";
import AppHeader from "@/components/AppHeader";
import { ALL_TOOLS, NAV_CATEGORIES } from "@/data/navigation";
import Main from "@/components/Main";
import Seo from "@/components/Seo";

/**
 * Single overview page that lists every tool grouped by category, with
 * a search box. This is the "I forgot where the molar mass calculator
 * lives" page - keep it dense, scannable, and fast to filter.
 */
export default function Explore() {
  const [query, setQuery] = useState("");
  const location = useLocation();
  const HEADER_OFFSET = 64; // 56px sticky header + 8px breathing room

  /** Currently highlighted category key (driven by hash + scroll spy). */
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  /**
   * Smoothly scroll a category section into view, accounting for the sticky
   * header. Falls back to a manual rAF-driven tween when the browser does
   * not support smooth `window.scrollTo({behavior:'smooth'})` (older Safari,
   * some embedded WebViews) so the offset still lands correctly everywhere.
   */
  const scrollToCategory = useCallback(
    (key: string, behavior: ScrollBehavior = "smooth") => {
      const el =
        sectionRefs.current[key] ?? document.getElementById(`cat-${key}`);
      if (!el) return;
      const targetTop =
        el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;

      const prefersReduced =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const wantSmooth = behavior === "smooth" && !prefersReduced;

      // Feature-detect smooth scrolling support.
      const supportsSmooth =
        typeof document !== "undefined" &&
        "scrollBehavior" in document.documentElement.style;

      if (!wantSmooth || !supportsSmooth) {
        if (wantSmooth && !supportsSmooth) {
          // Manual easing fallback (~400ms ease-out).
          const startY = window.pageYOffset;
          const distance = targetTop - startY;
          const duration = 400;
          const startTime = performance.now();
          const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
          const step = (now: number) => {
            const elapsed = now - startTime;
            const t = Math.min(1, elapsed / duration);
            window.scrollTo(0, startY + distance * easeOutCubic(t));
            if (t < 1) window.requestAnimationFrame(step);
          };
          window.requestAnimationFrame(step);
        } else {
          window.scrollTo(0, targetTop);
        }
      } else {
        window.scrollTo({ top: targetTop, behavior: "smooth" });
      }
      setActiveCat(key);
    },
    [],
  );

  /**
   * Resolve a hash like `#cat-learn` (or just `#learn`) to a category key
   * we know about, so the URL stays robust even if the heading id format
   * shifts later or the section is initially collapsed.
   */
  const resolveHashKey = useCallback((rawHash: string): string | null => {
    const hash = rawHash.replace(/^#/, "");
    if (!hash) return null;
    const direct = NAV_CATEGORIES.find(
      (c) => `cat-${c.key}` === hash || c.key === hash,
    );
    return direct?.key ?? null;
  }, []);

  // Scroll to the hashed category whenever the URL hash changes.
  // Retry briefly in case the section mounts after first paint
  // (e.g. inside a collapsed panel that just expanded).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = resolveHashKey(location.hash);
    if (!key) return;

    let attempts = 0;
    let raf = 0;
    const tryScroll = () => {
      const el =
        sectionRefs.current[key] ?? document.getElementById(`cat-${key}`);
      if (el) {
        scrollToCategory(key, "smooth");
        return;
      }
      if (attempts++ < 10) {
        raf = window.requestAnimationFrame(tryScroll);
      }
    };
    raf = window.requestAnimationFrame(tryScroll);
    return () => window.cancelAnimationFrame(raf);
  }, [location.hash, resolveHashKey, scrollToCategory]);

  // Scroll spy: highlight whichever category section is currently
  // crossing a band just below the sticky header.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sections = NAV_CATEGORIES
      .map((c) => sectionRefs.current[c.key])
      .filter((el): el is HTMLElement => Boolean(el));
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry whose top is closest to (but past) the header line.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              a.boundingClientRect.top - b.boundingClientRect.top,
          );
        if (visible[0]) {
          const key = (visible[0].target as HTMLElement).dataset.catKey;
          if (key) setActiveCat(key);
        }
      },
      {
        // Activation band sits just below the header and ends ~60% down the viewport.
        rootMargin: `-${HEADER_OFFSET + 8}px 0px -40% 0px`,
        threshold: 0,
      },
    );

    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // Re-run when filtering changes which sections are in the DOM.
  }, [query]);

  // Scroll to the category section when arriving via a hash (e.g. #cat-learn).
  // Offset by the sticky header height so the heading isn't hidden underneath.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;

    const HEADER_OFFSET = 64; // 56px header + 8px breathing room
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    // Defer until after paint so the section is mounted and laid out.
    requestAnimationFrame(() => {
      const el = document.getElementById(hash);
      if (!el) return;
      const top =
        el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
      window.scrollTo({
        top,
        behavior: prefersReduced ? "auto" : "smooth",
      });
    });
  }, []);

  const normalizedQuery = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!normalizedQuery) return null;
    return new Set(
      ALL_TOOLS.filter(
        (t) =>
          t.label.toLowerCase().includes(normalizedQuery) ||
          t.description.toLowerCase().includes(normalizedQuery) ||
          (t.keywords ?? "").toLowerCase().includes(normalizedQuery) ||
          t.categoryLabel.toLowerCase().includes(normalizedQuery),
      ).map((t) => t.path),
    );
  }, [normalizedQuery]);

  const totalMatches = matches ? matches.size : ALL_TOOLS.length;

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Explore All Tools — ElementPad" description="Browse every ElementPad chemistry tool, game, and lesson in one place: periodic table, equation balancer, virtual lab, worksheets, and more." path="/explore" />
      <AppHeader />
      {/* Skip link — visible only when focused. Lets keyboard users jump
          past the search input and category jump-bar straight to the
          currently active (or first) category section. */}
      <a
        href={`#cat-${activeCat ?? NAV_CATEGORIES[0].key}`}
        onClick={(e) => {
          e.preventDefault();
          const key = activeCat ?? NAV_CATEGORIES[0].key;
          if (window.history.replaceState) {
            window.history.replaceState(null, "", `#cat-${key}`);
          }
          scrollToCategory(key);
          // Move focus to the section so screen readers/keyboard land there.
          const el = document.getElementById(`cat-${key}`);
          if (el) {
            el.setAttribute("tabindex", "-1");
            (el as HTMLElement).focus({ preventScroll: true });
          }
        }}
        className="absolute left-3 top-3 z-50 -translate-y-20 focus:translate-y-0 transition-transform inline-flex items-center px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        Skip to {activeCat
          ? NAV_CATEGORIES.find((c) => c.key === activeCat)?.label
          : "content"}
      </a>
      <Main className="max-w-[1200px] mx-auto px-4 py-8">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-1">All tools</h1>
          <p className="text-sm text-muted-foreground">
            Every ElementPad tool, grouped by what you want to do.
          </p>
        </header>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            aria-hidden="true"
          />
          <label htmlFor="explore-search" className="sr-only">
            Search tools
          </label>
          <input
            id="explore-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tools by name, topic, or keyword…"
            className="w-full h-10 pl-9 pr-9 rounded-lg border border-input bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground"
            >
              <X size={14} aria-hidden="true" />
            </button>
          )}
          {normalizedQuery && (
            <p
              className="mt-2 text-xs text-muted-foreground"
              aria-live="polite"
            >
              {totalMatches} {totalMatches === 1 ? "result" : "results"}
            </p>
          )}
        </div>

        {/* Category jump-bar — also acts as the active-section indicator.
            Implements roving-tabindex keyboard nav (Arrow keys, Home/End). */}
        <nav
          aria-label="Jump to category"
          className="sticky top-14 z-20 -mx-4 px-4 py-2 mb-6 bg-background/80 backdrop-blur-md border-b border-border/60"
        >
          <ul role="tablist" className="flex flex-wrap gap-1.5">
            {NAV_CATEGORIES.map((cat, idx) => {
              const CatIcon = cat.icon;
              const isActive = activeCat === cat.key;
              // Roving tabindex: focused/active tab is in the tab order;
              // others are reachable only via arrow keys.
              const isFocusable =
                isActive || (!activeCat && idx === 0);
              return (
                <li key={cat.key} role="presentation">
                  <a
                    href={`#cat-${cat.key}`}
                    role="tab"
                    id={`cat-tab-${cat.key}`}
                    tabIndex={isFocusable ? 0 : -1}
                    aria-selected={isActive}
                    aria-controls={`cat-${cat.key}`}
                    aria-current={isActive ? "true" : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      if (window.history.replaceState) {
                        window.history.replaceState(
                          null,
                          "",
                          `#cat-${cat.key}`,
                        );
                      }
                      scrollToCategory(cat.key);
                    }}
                    onKeyDown={(e) => {
                      const keys = [
                        "ArrowRight",
                        "ArrowLeft",
                        "ArrowDown",
                        "ArrowUp",
                        "Home",
                        "End",
                        "Enter",
                        " ",
                      ];
                      if (!keys.includes(e.key)) return;
                      e.preventDefault();

                      if (e.key === "Enter" || e.key === " ") {
                        if (window.history.replaceState) {
                          window.history.replaceState(
                            null,
                            "",
                            `#cat-${cat.key}`,
                          );
                        }
                        scrollToCategory(cat.key);
                        return;
                      }

                      const last = NAV_CATEGORIES.length - 1;
                      let nextIdx = idx;
                      if (e.key === "ArrowRight" || e.key === "ArrowDown")
                        nextIdx = idx === last ? 0 : idx + 1;
                      else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
                        nextIdx = idx === 0 ? last : idx - 1;
                      else if (e.key === "Home") nextIdx = 0;
                      else if (e.key === "End") nextIdx = last;

                      const nextKey = NAV_CATEGORIES[nextIdx].key;
                      const nextEl = document.getElementById(
                        `cat-tab-${nextKey}`,
                      ) as HTMLAnchorElement | null;
                      nextEl?.focus();
                      // Move highlight as user arrows through; URL updates too.
                      if (window.history.replaceState) {
                        window.history.replaceState(
                          null,
                          "",
                          `#cat-${nextKey}`,
                        );
                      }
                      setActiveCat(nextKey);
                    }}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                      isActive
                        ? "border-primary/50 bg-primary/10 text-primary"
                        : "border-border bg-card text-muted-foreground hover:text-foreground hover:border-muted-foreground/40"
                    }`}
                  >
                    <CatIcon size={12} aria-hidden="true" />
                    {cat.label}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Categories */}
        <div className="space-y-8">
          {NAV_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const visibleTools = matches
              ? cat.tools.filter((t) => matches.has(t.path))
              : cat.tools;
            if (visibleTools.length === 0) return null;
            const isActive = activeCat === cat.key;
            return (
              <section
                key={cat.key}
                id={`cat-${cat.key}`}
                data-cat-key={cat.key}
                ref={(el) => {
                  sectionRefs.current[cat.key] = el;
                }}
                aria-labelledby={`cat-${cat.key}-heading`}
                className={`scroll-mt-20 rounded-lg transition-colors ${
                  isActive ? "ring-1 ring-primary/30 bg-primary/[0.02]" : ""
                }`}
              >
                <div className="flex items-baseline gap-2 mb-3 pb-2 border-b border-border px-2 pt-2">
                  <Icon size={15} className="text-primary self-center" aria-hidden="true" />
                  <h2
                    id={`cat-${cat.key}-heading`}
                    className="text-base font-semibold text-foreground"
                  >
                    {cat.label}
                  </h2>
                  <span className="text-xs text-muted-foreground">
                    {cat.tagline}
                  </span>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 px-2 pb-2">
                  {visibleTools.map((tool) => {
                    const ToolIcon = tool.icon;
                    return (
                      <li key={tool.path}>
                        <Link
                          to={tool.path}
                          className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card hover:border-primary/40 hover:bg-accent/30 transition-colors h-full"
                        >
                          <span className="flex-shrink-0 w-8 h-8 rounded-md bg-muted flex items-center justify-center">
                            <ToolIcon size={15} className="text-primary" aria-hidden="true" />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-foreground leading-tight">
                              {tool.label}
                            </span>
                            <span className="block text-xs text-muted-foreground leading-snug mt-1">
                              {tool.description}
                            </span>
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}

          {matches && totalMatches === 0 && (
            <p className="text-sm text-muted-foreground italic">
              No tools match "{query}". Try a different keyword.
            </p>
          )}
        </div>
      </Main>
    </div>
  );
}